import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { feature, mesh } from 'topojson-client';
import usTopology from '../content/us-states-albers-10m.json';

// States with RangeSafe commercial presence, painted RangeSafe red on the map
// (us-atlas state names).
const RED_STATES = new Set([
  'Florida',
  'Georgia',
  'Massachusetts',
  'California',
  'Arizona',
  'Colorado',
  'South Carolina',
  'North Carolina',
  'Illinois',
  'Missouri',
  'Alabama',
  'Utah',
  'South Dakota',
  'Hawaii',
  'Kansas',
  'Washington',
  'Oregon',
  'New York',
  'Connecticut',
  'Texas',
  'Tennessee',
  'Ohio',
  'Virginia',
]);

// RangeSafe headquarters, Bracey, Virginia. Marked with the white brand icon.
const HQ_LONLAT = [-78.137, 36.592];

// Commercial reach metrics, kept in sync with the map above.
export const REACH_METRICS = [
  { value: '100+', label: 'Cities served' },
  { value: '23', label: 'States served' },
  { value: '4', label: 'International markets' },
];

export default function USPresenceMap() {
  const reduce = useReducedMotion();
  const [hqHover, setHqHover] = useState(false);

  const { stateFeatures, statePaths, nationPath, redOrder, hqPoint } = useMemo(() => {
    // The us-atlas topology is pre-projected (AlbersUSA, 975x610 viewport);
    // drawing it needs a bare geoPath.
    const path = geoPath();
    const features = feature(usTopology, usTopology.objects.states).features;
    const statePaths = features.map((f) => path(f));
    const nationPath = path(mesh(usTopology, usTopology.objects.nation));
    const redOrder = new Map();
    features.forEach((f) => {
      if (RED_STATES.has(f.properties.name)) {
        redOrder.set(f.properties.name, redOrder.size);
      }
    });
    // Project Bracey, VA into the same pre-projected AlbersUSA space the
    // us-atlas 10m topology uses (scale 1300, translate [487.5, 305]).
    const hqPoint = geoAlbersUsa().scale(1300).translate([487.5, 305])(HQ_LONLAT);
    return { stateFeatures: features, statePaths, nationPath, redOrder, hqPoint };
  }, []);

  return (
    <motion.svg
      viewBox="0 0 975 610"
      className="block w-full"
      role="img"
      aria-label="Map of the United States with RangeSafe-served states painted in RangeSafe red"
      data-testid="us-presence-map"
      initial={reduce ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once: true, amount: 0.25 }}
    >
      <motion.g
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { duration: 0.7, ease: 'easeOut' } },
        }}
      >
        {stateFeatures.map((f, i) => {
          const name = f.properties.name;
          const served = RED_STATES.has(name);
          const order = redOrder.get(name);
          return served ? (
            <motion.path
              key={name}
              d={statePaths[i]}
              fill="#DC2626"
              stroke="#E8E8EC"
              strokeWidth="0.9"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { duration: 0.49, ease: 'easeOut', delay: 0.56 + order * 0.07 } },
              }}
              data-testid={`state-${name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <title>{`${name} — RangeSafe commercial presence`}</title>
            </motion.path>
          ) : (
            <path key={name} d={statePaths[i]} fill="#0E0E10" stroke="#9A9AA2" strokeWidth="0.9" data-testid={`state-${name.toLowerCase().replace(/\s+/g, '-')}`}>
              <title>{name}</title>
            </path>
          );
        })}
        <path d={nationPath} fill="none" stroke="#FFFFFF" strokeWidth="1.6" />

        {hqPoint && (
          <motion.g
            data-testid="hq-marker-bracey"
            aria-label="RangeSafe headquarters, Bracey, Virginia"
            onMouseEnter={() => setHqHover(true)}
            onMouseLeave={() => setHqHover(false)}
            style={{ cursor: 'pointer' }}
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { duration: 0.5, ease: 'easeOut', delay: 1.9 } },
            }}
          >
            <circle cx={hqPoint[0]} cy={hqPoint[1]} r="12" fill="#232326" stroke="#FFFFFF" strokeWidth="1.2" />
            <image
              href="/assets/brand/rangesafe-icon.png"
              x={hqPoint[0] - 9}
              y={hqPoint[1] - 10}
              width="18"
              height="20"
              preserveAspectRatio="xMidYMid meet"
            />
            {hqHover && (
              <g pointerEvents="none" data-testid="hq-tooltip">
                <rect
                  x={hqPoint[0] - 292}
                  y={hqPoint[1] - 118}
                  width="320"
                  height="92"
                  rx="8"
                  fill="#0B0B0D"
                  stroke="#FFFFFF"
                  strokeWidth="1.4"
                />
                <polygon
                  points={`${hqPoint[0] - 12},${hqPoint[1] - 26} ${hqPoint[0] + 6},${hqPoint[1] - 26} ${hqPoint[0]},${hqPoint[1] - 14}`}
                  fill="#0B0B0D"
                  stroke="#FFFFFF"
                  strokeWidth="1.4"
                />
                <text
                  x={hqPoint[0] - 132}
                  y={hqPoint[1] - 76}
                  textAnchor="middle"
                  fill="#FFFFFF"
                  fontSize="26"
                  fontWeight="700"
                >
                  RangeSafe Headquarters
                </text>
                <text
                  x={hqPoint[0] - 132}
                  y={hqPoint[1] - 44}
                  textAnchor="middle"
                  fill="#C9C9CF"
                  fontSize="21"
                >
                  Bracey, VA
                </text>
              </g>
            )}
          </motion.g>
        )}
      </motion.g>
    </motion.svg>
  );
}
